import * as React from 'react';
import * as Autosuggest from 'react-autosuggest';

const DEFAULT_THEME = {
  container: 'typeahead',
  input: 'form-control search',
  suggestionsContainer: 'dropdown open',
  suggestionsList: 'dropdown-menu',
  suggestion: '',
  suggestionFocused: 'active',
  suggestionHighlighted: 'active'
};

interface SearchProps<T> {
  autoFocus?: boolean;
  className?: string;
  debounce?: number;
  load: (query: string) => Promise<T[]>;
  maxLength?: number;
  minLength?: number;
  onSelect: (suggestion?: T) => void;
  clearOnSelect?: boolean;
  onSubmit?: (value?: string) => void;
  onTextChange?: (value?: string) => void;
  placeholder: string;
  renderSuggestion?: (suggestion: T) => JSX.Element;
  renderValue?: (suggestion: T) => string;
  value?: T;
  highlightFirst?: boolean;
}

interface DefaultSearchProps { autoFocus: boolean; debounce: number; maxLength: number; minLength: number; }

interface SearchState<T> { isLoading: boolean; suggestions: T[]; value: string; }

export class Search<T> extends React.Component<SearchProps<T>, SearchState<T>> {
  private static defaultProps: DefaultSearchProps = {
    autoFocus: false,
    debounce: 250,
    maxLength: 200,
    minLength: 3,
  };
  private static LABEL_KEYS = ['displayName', 'name'];

  private lastRequestId = 0;
  private debouncedLoad: Autosuggest.SuggestionsFetchRequested;
  private input: HTMLInputElement | null;

  constructor(props: SearchProps<T>) {
    super(props);
    this.state = {
      value: '',
      suggestions: [],
      isLoading: false
    };
    const debounce = this.props.debounce !== undefined ? this.props.debounce : Search.defaultProps.debounce;
    this.debouncedLoad = this.debounce(this.load, debounce);
  }

  componentDidUpdate(prevProps: SearchProps<T>) {
    if (prevProps.value !== this.props.value) {
      this.setState({ value: this.props.value ? this.renderValue(this.props.value) : '' });
    }
  }

  componentDidMount() {
    if (this.props.value) {
      this.setState({ value: this.props.value ? this.renderValue(this.props.value) : '' });
    }
  }

  debounce = (func: Autosuggest.SuggestionsFetchRequested, ms: number) => {
    let timer: any = null;
    return (params: Autosuggest.SuggestionsFetchRequestedParams) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(params), ms);
    };
  };

  renderValue = (suggestion: T) => {
    if (this.props.renderValue) {
      return this.props.renderValue(suggestion);
    } else if (typeof suggestion === 'string' || typeof suggestion === 'number') {
      return suggestion.toString();
    }

    const labelKey = Search.LABEL_KEYS.find(key => key in suggestion && suggestion[key]);

    if (!labelKey) {
      throw new Error('renderValue should be provided');
    }

    return suggestion[labelKey];
  };

  renderSuggestion = (suggestion: T) => {
    return this.props.renderSuggestion ?
      this.props.renderSuggestion(suggestion) :
      <a href="#">{this.renderValue(suggestion)}</a>;
  };

  onTextChange = (event: any, { newValue }: Autosuggest.ChangeEvent) => {
    const maxLength = this.props.maxLength === undefined ?
      Search.defaultProps.maxLength : this.props.maxLength;

    if (newValue.length > maxLength) {
      return;
    }

    this.setState(
      { value: newValue },
      () => {
        if (this.props.onTextChange) {
          this.props.onTextChange(newValue || undefined);
        }
      }
    );
  };

  onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (this.props.onSubmit && event.key === 'Enter') {
      event.preventDefault();
      this.props.onSubmit(this.state.value || undefined);
    }
  };

  load = ({ value }: Autosuggest.SuggestionsFetchRequestedParams) => {
    const maxLength = this.props.maxLength === undefined ?
      Search.defaultProps.maxLength : this.props.maxLength;
    const minLength = this.props.minLength === undefined ?
      Search.defaultProps.minLength : this.props.minLength;

    if (value.length < minLength || value.length > maxLength) {
      return;
    }

    this.setState({ isLoading: true });

    const requestId = this.lastRequestId + 1;
    this.lastRequestId = requestId;

    this.props.load(value)
      .then(suggestions => {
        if (requestId === this.lastRequestId) {
          this.setState({ isLoading: false, suggestions });
        }
      });
  };

  onSuggestionsClearRequested = () => this.setState({ suggestions: [] });

  onSuggestionSelected = (
    event: React.MouseEvent<Autosuggest>,
    { suggestion }: Autosuggest.SuggestionSelectedEventData<T>
  ) => {
    event.preventDefault();

    this.props.onSelect(suggestion || undefined);

    if (this.props.clearOnSelect) {
      this.setState({ value: '' });
    }
  };

  shouldRenderSuggestions = () => true;

  focus = () => this.input && this.input.focus();

  render() {
    const { value, isLoading, suggestions } = this.state;
    const inputProps = {
      'aria-label': this.props.placeholder || 'Search',
      autoFocus: this.props.autoFocus,
      placeholder: this.props.placeholder,
      value,
      onChange: this.onTextChange,
      onKeyDown: this.onKeyDown
    };
    const theme = { ...DEFAULT_THEME, input: 'form-control ' + (isLoading ? 'loading' : 'search') };

    return (
      <div className={this.props.className || ''}>
        <Autosuggest
          getSuggestionValue={this.renderValue}
          inputProps={inputProps}
          highlightFirstSuggestion={this.props.highlightFirst || false}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested}
          onSuggestionsFetchRequested={this.debouncedLoad}
          onSuggestionSelected={this.onSuggestionSelected}
          renderSuggestion={this.renderSuggestion}
          shouldRenderSuggestions={this.shouldRenderSuggestions}
          suggestions={suggestions}
          ref={(autosuggest: any) => this.input = autosuggest && autosuggest.input}
          theme={theme}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/gs-core/client/react/Search/Search.tsx